import { clsx } from 'clsx';
import { formatAmountWithSpaces } from '@/lib/amountUtils';
import {
  bannerLightElement,
  bannerLightElement1,
  bannerLightElement2,
  bannerLightElement3,
  bannerVoltL,
  bannerVoltOZero,
  bannerVoltT,
  bannerVoltV,
  infiniteArt,
  infiniteBadge,
  infiniteVoltLogo,
  lightElement,
  lightElement1,
  lightElement2,
  lightElement3,
  oneArt,
  oneBadge,
  visaLogo,
  voltL,
  voltOOne,
  voltT,
  voltV,
  zeroArt,
} from '@/assets/images/offer/tiers';
import styles from './VoltTierFace.module.scss';

// Zero is only used in the banner, One and Infinite only as full cards; the
// artwork is exported at those sizes.
export type VoltTier = 'zero' | 'one' | 'infinite';

export interface VoltTierFaceProps {
  tier: VoltTier;
  amount: number;
  className?: string;
}

const ART: Record<VoltTier, string> = {
  zero: zeroArt,
  one: oneArt,
  infinite: infiniteArt,
};

function TierLight({ isBanner }: { isBanner: boolean }) {
  const element = isBanner ? bannerLightElement : lightElement;
  const elementAlt = isBanner ? bannerLightElement3 : lightElement3;
  const ray = isBanner ? bannerLightElement1 : lightElement1;
  const rayShort = isBanner ? bannerLightElement2 : lightElement2;

  // Two stacked groups of the same geometry (the second outlined), placed at
  // the design's group offsets: mostly outside the face, only the edges show.
  return (
    <div
      className={clsx(styles.light, { [styles['light--banner']]: isBanner })}
      aria-hidden
    >
      {[element, elementAlt].map((glow, index) => (
        <div
          key={glow}
          className={clsx(styles.lightGroup, styles[`lightGroup${index}`], {
            [styles.lightOutlined]: index === 1,
          })}
        >
          <div className={clsx(styles.layer, styles.glow)}>
            <div className={styles.glowShape}>
              <div className={styles.glowInset}>
                <img src={glow} alt="" className={styles.fill} />
              </div>
            </div>
          </div>
          <div className={clsx(styles.layer, styles.beam1)}>
            <div className={styles.beamShape} />
          </div>
          <div className={clsx(styles.layer, styles.beam2)}>
            <div className={styles.beamShape} />
          </div>
          {index === 1 && (
            <div className={clsx(styles.layer, styles.beam3)}>
              <div className={styles.beamShape} />
            </div>
          )}
          <div className={clsx(styles.layer, styles.ray1)}>
            <div className={styles.rayShape}>
              <div className={styles.rayInset}>
                <img src={ray} alt="" className={styles.fill} />
              </div>
            </div>
          </div>
          <div className={clsx(styles.layer, styles.ray2)}>
            <div className={styles.rayShape}>
              <div className={styles.rayInset}>
                <img src={ray} alt="" className={styles.fill} />
              </div>
            </div>
          </div>
          <div className={clsx(styles.layer, styles.ray3)}>
            <div className={styles.rayShape}>
              <div className={styles.rayInset}>
                <img src={rayShort} alt="" className={styles.fill} />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function TierLogo({ tier }: { tier: VoltTier }) {
  if (tier === 'infinite') {
    return <img src={infiniteVoltLogo} alt="Volt" className={styles.logoInfinite} />;
  }

  const isBanner = tier === 'zero';
  const letters = isBanner
    ? [bannerVoltV, bannerVoltOZero, bannerVoltL, bannerVoltT]
    : [voltV, voltOOne, voltL, voltT];

  return (
    <div className={styles.logo} aria-label="Volt" role="img">
      {letters.map((letter, index) => (
        <img
          key={letter}
          src={letter}
          alt=""
          className={styles[`logoLetter${index}`]}
        />
      ))}
    </div>
  );
}

function TierBadge({ tier }: { tier: VoltTier }) {
  if (tier === 'zero') {
    return (
      <div className={styles.badgeZero}>
        <span className={styles.badgeZeroText}>0</span>
      </div>
    );
  }

  return (
    <img
      src={tier === 'one' ? oneBadge : infiniteBadge}
      alt=""
      className={tier === 'one' ? styles.badgeOne : styles.badgeInfinite}
    />
  );
}

/**
 * The card face of a Volt tier (Zero / One / Infinite) with its marketing
 * amount, as designed for the preliminary offers screen.
 */
export function VoltTierFace({ tier, amount, className }: VoltTierFaceProps) {
  const isBanner = tier === 'zero';

  return (
    <div className={clsx(styles.root, styles[`root--${tier}`], className)}>
      <div className={clsx(styles.art, styles[`art--${tier}`])} aria-hidden>
        <div className={styles.artRotated}>
          <img src={ART[tier]} alt="" className={styles.artImage} />
          {tier === 'one' && <div className={styles.artShade} />}
        </div>
      </div>

      {tier !== 'infinite' && <TierLight isBanner={isBanner} />}

      <div className={styles.header}>
        <TierLogo tier={tier} />
        <TierBadge tier={tier} />
      </div>

      <div className={styles.bottom}>
        <div className={styles.balance}>
          <p className={styles.balanceLabel}>Până la</p>
          <p className={styles.balanceAmount}>
            <span className={styles.amount}>{formatAmountWithSpaces(amount)}</span>
            <span className={styles.currency}>lei</span>
          </p>
        </div>
        {tier === 'infinite' && (
          <div className={styles.visa}>
            <img src={visaLogo} alt="Visa" className={styles.visaLogo} />
          </div>
        )}
      </div>
    </div>
  );
}
